import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LEAD_STAGES, LeadStages } from 'models/Lead';
import { Listing, ListingStage, ListingType } from 'models/Listing';
import {
  AskingPriceSelect,
  DealOutcome,
  DealStage,
  LeadStagesCopies,
} from 'models/DealRoom';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import PortfolioStage from './components/PortfolioStage';
import useMatchMedia from 'hooks/useMatchMedia';
import HandIcon from 'icons/Hand.icon';
import DocumentIcon from 'icons/Document.icon';
import { User } from 'models/User';
import { WithdrawButton } from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Buyer/components/BuyerActions/WithdrawButton';
import TagsContainer from './components/TagsContainer';
import usePastTime from 'hooks/usePastTime';
import ListingPrices from 'components/ListingPrices';
import SellerInfo from './components/SellerInfo';
import { Industry } from 'models/Industry';
import { isSuperAdminOrOwner } from 'utils';
import ListingMainImageWithUploadOption from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/components/ListingMainImageWithUploadOption';
import { DropzoneFile } from 'components/Dropzone/utils';

import './ListingPortfolioCard.styles.scss';
import './ListingPortfolioCard.styles.responsive.scss';

type ListingPortfolioCardProps = {
  className?: string;
  dealStage: DealStage;
  listingStage: ListingStage;
  title: string;
  dealOutcome: DealOutcome;
  otherDealOutcome: string;
  isColleague: boolean;
  date: Date;
  dealRoomId?: string;
  asBuyer?: boolean;
  listingType: ListingType;
  industry: Industry;
  expirationDate?: Date;
  seekingExpressionOfInterestDate?: string;
  ownerId: string;
  owner: Partial<User>;
  enquiryId: string;
  numberOfAcceptedLeads: number;
  numberOfConfirmedLeads: number;
  numberOfEnquiredLeads: number;
  numberOfOfferedLeads: number;
  progressedStep: number;
  leadProgressedStep?: number;
  buyerStep?: LeadStages;
  withdrawnInterest?: boolean;
  confirmedInterest?: boolean;
  leadId: string | null;
  listing: Partial<Listing>;
  priceGuide?: AskingPriceSelect;
  amount?: number | null;
  amountFrom?: number | null;
  amountTo?: number | null;
  franchisorFeePercentage?: number;
  numberOfLeads?: number;
  numberOfFiles?: number;
  isRWC?: boolean;
  allowListingImageUpload?: boolean;
  leadStagesCopies: LeadStagesCopies;
  onWithdrawSuccess?: () => void;
  updateGallery?: (dealId: string, gallery: DropzoneFile[]) => void;
};

const ListingPortfolioCard: React.FC<ListingPortfolioCardProps> = (props) => {
  const {
    className,
    title,
    dealStage,
    listingStage,
    dealOutcome,
    otherDealOutcome,
    isColleague,
    date,
    dealRoomId,
    asBuyer,
    listingType,
    industry,
    expirationDate,
    seekingExpressionOfInterestDate,
    owner,
    ownerId,
    enquiryId,
    numberOfAcceptedLeads,
    numberOfConfirmedLeads,
    numberOfEnquiredLeads,
    numberOfOfferedLeads,
    progressedStep,
    leadProgressedStep,
    withdrawnInterest,
    confirmedInterest,
    buyerStep,
    leadId,
    listing,
    priceGuide,
    amount,
    amountFrom,
    amountTo,
    franchisorFeePercentage,
    numberOfFiles,
    numberOfLeads,
    isRWC,
    leadStagesCopies,
    allowListingImageUpload = false,
    onWithdrawSuccess,
    updateGallery,
  } = props;

  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const {
    currentUser: { _id: currentUserId },
    isSuperAdmin,
  } = useContext(CurrentUserContext);

  const { t } = useTranslation();
  const pastTime = usePastTime();

  const isAbovePhablet = useMatchMedia('isAbovePhablet');

  const classes = classNames('listing-portfolio-card', className);

  const isOwnerOrSuperAdmin = isSuperAdminOrOwner(
    isSuperAdmin,
    ownerId,
    currentUserId,
  );

  const companyName = owner.companyName;
  const canWithdrawn =
    asBuyer &&
    !withdrawnInterest &&
    (buyerStep !== LEAD_STAGES.ACCEPT || !confirmedInterest);

  const showTags = !asBuyer || isAbovePhablet;

  const rerouteLink = useMemo(
    () => (asBuyer ? enquiryId : dealRoomId),
    [asBuyer, dealRoomId, enquiryId],
  );

  const expiredDate = useMemo(() => {
    if (!seekingExpressionOfInterestDate && !expirationDate) return null;

    if (!expirationDate) return new Date(seekingExpressionOfInterestDate);

    if (!seekingExpressionOfInterestDate) return new Date(expirationDate);

    const seekingDate = new Date(seekingExpressionOfInterestDate);
    const exprDate = new Date(expirationDate);

    return seekingDate < exprDate ? seekingDate : exprDate;
  }, [expirationDate, seekingExpressionOfInterestDate]);

  return (
    <div className={classes}>
      <div className="listing-portfolio-card__left">
        <div className="listing-portfolio-card__left__heading">
          <Link
            to={rerouteLink}
            className="listing-portfolio-card__left__heading__title"
          >
            {title}
          </Link>
          {(dealOutcome || industry) && (
            <Link
              to={rerouteLink}
              className="listing-portfolio-card__left__heading__deal-info"
            >
              {dealOutcome && (
                <span>
                  {dealOutcome === 'other' ? otherDealOutcome : t(dealOutcome)}
                </span>
              )}
              <span>{industry}</span>
            </Link>
          )}
        </div>

        {showTags && (
          <Link to={rerouteLink}>
            <TagsContainer
              isColleague={isColleague}
              dealStage={dealStage}
              listingStage={listingStage}
              isOwnerOrSuperAdmin={isOwnerOrSuperAdmin}
              listingType={listingType}
              date={date}
              expiredDate={expiredDate}
              withdrawnInterest={withdrawnInterest}
            />
          </Link>
        )}

        <Link to={rerouteLink}>
          {asBuyer ? (
            <ListingPrices
              askingPrice={listing?.askingPriceSelect}
              amount={listing?.amount}
              amountFrom={listing?.amountFrom}
              amountTo={listing?.amountTo}
              revenue={listing?.revenue}
              EBITDA={listing?.EBITDA}
              averageStockValue={listing?.averageStockValue}
              stockAtValue={listing?.stockAtValue}
              currency={listing?.currency}
            />
          ) : (
            <SellerInfo
              listing={listing}
              priceGuide={priceGuide}
              amount={amount}
              amountFrom={amountFrom}
              amountTo={amountTo}
              franchisorFeePercentage={franchisorFeePercentage}
              numberOfLeads={numberOfLeads}
              numberOfFiles={numberOfFiles}
            />
          )}
        </Link>

        {isAbovePhablet && (
          <PortfolioStage
            asBuyer={asBuyer}
            progressedStep={progressedStep}
            numberOfAcceptedLeads={numberOfAcceptedLeads}
            numberOfConfirmedLeads={numberOfConfirmedLeads}
            numberOfEnquiredLeads={numberOfEnquiredLeads}
            numberOfOfferedLeads={numberOfOfferedLeads}
            leadProgressedStep={leadProgressedStep}
            leadStagesCopies={leadStagesCopies}
          />
        )}

        <div className="listing-portfolio-card__left__actions">
          <Link to={rerouteLink} className="border">
            {isAbovePhablet && <DocumentIcon />}
            {t('manageDeal')}
          </Link>

          {canWithdrawn && (
            <WithdrawButton
              leadId={leadId}
              dealId={dealRoomId}
              isLoading={isWithdrawing}
              setIsLoading={setIsWithdrawing}
              onSuccess={onWithdrawSuccess}
            />
          )}

          {!asBuyer && (
            <Link to={`${rerouteLink}/leads`}>
              <HandIcon />
              {t('leads')}
            </Link>
          )}
        </div>

        {isSuperAdmin && (
          <p className="listing-portfolio-card__left__represented">
            {owner.lastSeen && <span>{pastTime(owner.lastSeen)}</span>}
            <span>{`${owner.firstName} ${owner.lastName}`}</span>
            {companyName && <span>{companyName}</span>}
          </p>
        )}
      </div>

      <div className="listing-portfolio-card__image-container">
        <ListingMainImageWithUploadOption
          listing={listing}
          isRWC={isRWC}
          isDisabledUpload={!allowListingImageUpload}
          updateGallery={updateGallery}
        />
      </div>
    </div>
  );
};

export default ListingPortfolioCard;
